import interpolateFunction from './interpolateFunction.js';
import { getDeep } from './getDeep.js';

const normalizeInterpolationValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  return value;
};

/**
 * Interpolates a string by replacing placeholders with corresponding values from a context object.
 *
 * @param {string} [str=''] - The string containing placeholders in the format `{{key}}` or `{{fn(arg1, arg2)}}`.
 * @param {Object} [context={}] - The context object containing values to replace placeholders.
 * @returns {string} The interpolated string with placeholders replaced by corresponding context values.
 *
 * @example
 * const context = {
 *   user: {
 *     name: 'John'
 *   }
 * };
 *
 * const result = interpolateValue('Hello, {{user.name}}!', context);
 * console.log(result); // Output: 'Hello, John!'
 *
 * const result2 = interpolateValue('Basic {{system.base64encode(credentials.clientId, credentials.clientSecret)}}', context);
 * console.log(result2); // Output: 'Basic dXNlcjEyMzpwYXNzd29yZDQ1Ng==' (base64 of 'user123:password456')
 *
 * const result3 = interpolateValue('Your ID is {{user.id}}.', context);
 * console.log(result3); // Output: 'Your ID is {{user.id}}.' (no match, returns original string)
 */
const interpolateValue = (str = '', context = {}) => {
  if (typeof str !== 'string') {
    return str;
  }

  return str.replace(/\{\{(.+?)\}\}/g, (match, expression) => {
    expression = expression.trim();

    const functionMatch = expression.match(/^(.+?)\((.*)\)$/);
    if (functionMatch) {
      return interpolateFunction({ str: match, functionMatch, context });
    }

    const value = normalizeInterpolationValue(getDeep(expression, context));
    return value !== undefined ? value : match;
  });
};

export default interpolateValue;
